/* eslint-disable react/forbid-prop-types */
import { useEffect } from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import useOnBoarding from 'hooks/useOnBoarding';
import { ONBOARDING_MAX_STEP } from 'features/onboarding/constants';
import { selectors as credentialsSelectors } from 'lib/entities/credentials';

// Local Components
import useProfileData from 'hooks/useProfileData';
import useNotificationListener from 'hooks/useNotificationListener';
import { effects as notificationsEffects } from 'features/edit-profile/slice';
import useChatListenerWsSocialV2 from 'hooks/useChatListenerWsSocialV2';
import { useGetReferralInfo } from 'features/community/hooks/useGetReferralInfo';
import { useGetChatUnreadCount } from 'stores/chat/useGetChatUnreadCount';
import TradingContextProvider from 'features/transaction/provider';
import Container, { Wrapper } from './styled';

// local redux slice
import { selectors } from './slice';
import { DATA_CY_MAIN_LAYOUT } from './__tests__/constant';

const HeaderWeb = dynamic(() => import('../Header/Web'), { ssr: false });
const HeaderMobile = dynamic(() => import('../Header/Mobile'), { ssr: false });
const SideWidget = dynamic(() => import('./SideWidget'), { ssr: false });
const OnBoarding = dynamic(() => import('features/onboarding'), { ssr: false });
const ModalTransactionSecurities = dynamic(
  () => import('global/TradingModal'),
  { ssr: false },
);
const ModalAuthSecurities = dynamic(
  () => import('features/securities/AuthModal'),
  {
    ssr: false,
  },
);
const ModalShareTradeManager = dynamic(
  () => import('features/sharetrade/components/ModalShareTradeManager'),
  { ssr: false },
);
const GlobalModalManager = dynamic(() => import('global/GlobalModalManager'), {
  ssr: false,
});
const ModalBonds = dynamic(
  () => import('global/TradingModal/RealTradingModal/ModalBonds'),
  { ssr: false },
);
const ModalUnavailable = dynamic(
  () => import('features/company/FRPage/ModalUnavailable'),
  { ssr: false },
);
const ModalErrorManager = dynamic(
  () => import('./ModalErrorMessage/ModalErrorManager'),
  { ssr: false },
);
const MultiPortoModalManager = dynamic(
  () => import('features/multi-porto/components/ModalManager'),
  { ssr: false },
);
const StreamErrorModalManager = dynamic(
  () => import('features/stream/ModalError/ModalManager'),
  { ssr: false },
);

function MainLayout({
  children,
  isFlexibleWidth,
  maxWidth,
  background,
  width,
  fullPage = false,
}) {
  // Library hooks
  const dispatch = useDispatch();
  const breakpoints = useBreakpoint();

  // Zustand store
  const fetchChatUnreadCount = useGetChatUnreadCount((s) => s.fetchUnreadCount);
  const fetchCommunityInfo = useGetReferralInfo((s) => s.fetchCommunityInfo);

  // Redux store
  const { isLoggedIn } = useSelector(credentialsSelectors);
  const { isSideWidgetOpen, isHeaderMinimized } = useSelector(selectors);

  // Custom hooks
  const { onboardingProgress } = useOnBoarding();
  useProfileData();

  // TODO: parse isLoggedIn to hooks props to prevent inconsistent hooks call
  if (isLoggedIn) {
    useNotificationListener();
    useChatListenerWsSocialV2();
  }

  // Reactive variable
  const isShowOnboarding = onboardingProgress < ONBOARDING_MAX_STEP;
  const props = width ? { style: { width } } : {};

  useEffect(() => {
    if (isLoggedIn) {
      fetchCommunityInfo();
      fetchChatUnreadCount();
      dispatch(notificationsEffects.getNotifications());
    }
  }, [isLoggedIn]);

  // React render
  if (isShowOnboarding) {
    return <OnBoarding progress={onboardingProgress} />;
  }
  return (
    <div data-cy={DATA_CY_MAIN_LAYOUT}>
      {breakpoints.md ? (
        <HeaderWeb
          isSideWidgetOpen={isSideWidgetOpen}
          isHeaderMinimized={isHeaderMinimized}
        />
      ) : (
        <HeaderMobile />
      )}

      <SideWidget />
      <Wrapper
        background={background}
        isSideWidgetOpen={isSideWidgetOpen}
        {...props}
      >
        <Container
          id="main-container"
          isFlexibleWidth={isFlexibleWidth}
          maxWidth={maxWidth}
          isSideWidgetOpen={isSideWidgetOpen}
          fullPage={fullPage}
        >
          {children}
        </Container>
      </Wrapper>
      <ModalErrorManager />
      <ModalAuthSecurities />
      <TradingContextProvider>
        <ModalTransactionSecurities />
      </TradingContextProvider>
      <GlobalModalManager />
      <ModalBonds />
      <ModalShareTradeManager />

      {/* Modal Unavailable FR for Transaction */}
      <ModalUnavailable />

      {/* Multi Porto Modal */}
      <MultiPortoModalManager />

      {/* Stream Error Modal */}
      <StreamErrorModalManager />
    </div>
  );
}

MainLayout.propTypes = {
  children: PropTypes.node.isRequired,
  isFlexibleWidth: PropTypes.bool,
  maxWidth: PropTypes.number,
  background: PropTypes.string,
  width: PropTypes.any,
};

MainLayout.defaultProps = {
  isFlexibleWidth: false,
  maxWidth: undefined,
  background: 'transparent',
  width: undefined,
};

export default MainLayout;
